'use client'

import { useTranslations } from 'next-intl'
import { RefObject } from 'react'
import useSWRInfinite from 'swr/infinite'

import Swimlane from '@/app/[locale]/(worlds)/components/Swimlane'
import LoadMoreButton from '@/components/LoadMoreButton'
import SectionSeparator from '@/components/SectionSeparator'
import ViverseWorldCard from '@/components/ViverseWorldCard'
import { ViverseWorldCardSkeleton } from '@/components/ViverseWorldCardSkeleton'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { useDraggingScrollMotion } from '@/hooks/useDraggingScrollMotion'
import { useLoadMore } from '@/hooks/useLoadMore'
import { cn } from '@/lib/utils'
import { getCardType, type NonBannerCardType } from '@/lib/viverse-world-card'
import { ContentTypeCode, getPublicRoomsV3, Room, RoomDetail } from '@/services/cms'

const PAGE_SIZE = 12

type PageData = { rooms: RoomDetail[]; total: number }

type UserContentSectionProps = {
  sectionId: string
  titleKey: string
  contentTypes: ContentTypeCode | ContentTypeCode[]
  initialRooms: RoomDetail[]
  totalCount: number
  cmsAccountId: string
  cardType: NonBannerCardType
}

export default function UserContentSection({
  sectionId,
  titleKey,
  contentTypes,
  initialRooms,
  totalCount,
  cmsAccountId,
  cardType,
}: UserContentSectionProps) {
  const t = useTranslations()
  const { store } = useConfigsContext()
  const { isIosMobileApp } = useUAContext()
  const htcToken = store?.credentials?.htcToken

  const { data, size, setSize, isLoading, isValidating } = useSWRInfinite<PageData>(
    (pageIndex, previousPageData: PageData | null) => {
      if (previousPageData && previousPageData.rooms.length === 0) return null

      return `profile-rooms:${cmsAccountId}:${contentTypes}:${pageIndex}`
    },
    async (key) => {
      const pageIndex = Number(key.split(':').pop())

      const res = await getPublicRoomsV3({
        subject_id: cmsAccountId,
        total_vram_kb: -1,
        exclude_persona: true,
        content_types: Array.isArray(contentTypes) ? contentTypes : [contentTypes],
        scene_type: 'all',
        start: pageIndex * PAGE_SIZE,
        limit: PAGE_SIZE,
      })

      if (res?.status === 200) {
        return {
          rooms: res.results.results,
          total: res.results.pages?.total ?? res.results.results.length,
        }
      }

      return { rooms: [], total: 0 }
    },
    {
      fallbackData: [{ rooms: initialRooms, total: totalCount }],
      revalidateOnMount: false,
      revalidateFirstPage: false,
    },
  )

  const rooms = data?.flatMap((page) => page.rooms) ?? []
  const total = data?.[0]?.total ?? totalCount
  const enableLoadMore = total > rooms.length

  const sentinelRef = useLoadMore({
    enabled: enableLoadMore,
    isLoading,
    isValidating,
    setSize,
    size,
  })

  if (initialRooms.length === 0) return null

  const visibleRooms = isIosMobileApp ? rooms.filter((r) => r.is_voucher_room !== true) : rooms

  if (visibleRooms.length === 0 && !isLoading && !isValidating) return null
  const isFetching = isLoading || isValidating
  const skeletonCount = Math.min(PAGE_SIZE, total - rooms.length)

  const renderCards = (scrollRef: RefObject<HTMLDivElement | null>) => (
    <SwimlaneCards
      scrollRef={scrollRef}
      rooms={visibleRooms}
      htcToken={htcToken}
      isFetching={isFetching}
      sentinelRef={sentinelRef}
      skeletonCount={skeletonCount}
      cardType={cardType}
    />
  )

  return (
    <>
      <SectionSeparator />
      <div id={sectionId} className="scroll-mt-36 md:scroll-mt-40">
        <div className="md:hidden">
          <Swimlane
            title={t(titleKey)}
            renderContent={renderCards}
            data-section={`profile-${sectionId}`}
          />
        </div>

        <section className="hidden flex-col justify-between px-4 md:flex md:px-8 lg:px-18">
          <h2 className="header-4 relative mb-6 font-bold">{t(titleKey)}</h2>
          <div className="flex flex-col gap-10">
            <div className="flex flex-wrap gap-2">
              {visibleRooms.map((room, index) => (
                <ViverseWorldCard
                  className="profile-card-size"
                  key={`world-card-${room.id}-${index}`}
                  info={room}
                  hideLiked={!htcToken}
                  hidePremiumBadge={false}
                  cardType={room.content_type ? getCardType(room.content_type) : cardType}
                />
              ))}
              {isFetching &&
                Array.from({ length: skeletonCount }).map((_, i) => (
                  <ViverseWorldCardSkeleton key={`skeleton-${i}`} className="profile-card-size" />
                ))}
            </div>
            {enableLoadMore && (
              <div className="flex flex-1 justify-center">
                <LoadMoreButton isLoading={isFetching} onClick={() => setSize(size + 1)} />
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  )
}

function SwimlaneCards({
  scrollRef,
  rooms,
  htcToken,
  isFetching,
  sentinelRef,
  skeletonCount,
  cardType,
}: {
  scrollRef: RefObject<HTMLDivElement | null>
  rooms: (Room | RoomDetail)[]
  htcToken?: string
  isFetching: boolean
  sentinelRef: (node: Element | null) => void
  skeletonCount: number
  cardType: NonBannerCardType
}) {
  useDraggingScrollMotion(scrollRef)

  return (
    <div
      ref={scrollRef}
      className="no-scrollbar relative flex w-full flex-1 gap-4 overflow-x-auto overflow-y-hidden pl-4 md:pl-9"
    >
      {rooms.map((room, index) => (
        <ViverseWorldCard
          className={cn(
            'profile-card-size',
            index === rooms.length - 1 && !isFetching && 'mr-4 md:mr-6',
          )}
          key={`world-card-${room.id}-${index}`}
          info={room}
          hideLiked={!htcToken}
          hidePremiumBadge={false}
          cardType={room.content_type ? getCardType(room.content_type) : cardType}
        />
      ))}
      {isFetching &&
        Array.from({ length: skeletonCount }).map((_, i) => (
          <ViverseWorldCardSkeleton
            key={`skeleton-${i}`}
            className={cn('profile-card-size', i === skeletonCount - 1 && 'mr-4 md:mr-6')}
          />
        ))}
      <div ref={sentinelRef} className="min-w-px shrink-0" />
    </div>
  )
}
